/**
 * Supabase persistence for the site-config store.
 *
 * Maps the in-memory store (see site-config/types.ts) onto scms_site and
 * scms_site_page_content, and back. Only changed rows are written --
 * see ./diff.ts for why the whole store arrives on every save.
 *
 * SHAPE NOTE: PageContent is deliberately flat, with ~70 fields that every
 * block type may carry regardless of its own type. Those live in a single
 * JSONB `fields` column rather than 70 mostly-null columns; the mapping
 * functions below are the only place that split happens.
 */

import { cmsDb } from "./client"
import type { SiteConfigStore, Site, PageContent } from "../../site-config/types"
import { emptySiteConfigStore } from "../../site-config/types"
import type { StoreAdapter } from "./types"
import { diffRows, isEmptyDiff, getSnapshot, putSnapshot } from "./diff"

const SITE_TABLE = "scms_site"
const CONTENT_TABLE = "scms_site_page_content"

/** Columns broken out of PageContent; everything else goes into `fields`. */
const CONTENT_OWN_COLUMNS = new Set(["id", "type", "title", "translations", "oldid", "createdAt", "updatedAt"])

function siteToRow(site: Site, teamId: string) {
  return {
    id: site.id,
    team_id: teamId,
    name: site.name ?? null,
    base_url: site.baseUrl ?? null,
    logo: site.logo ?? null,
    default_locale: site.defaultLocale ?? null,
    locales: site.locales ?? [],
    google_tag: site.googleTag ?? [],
    transifex_project: site.transifexProject ?? null,
    google_translate: site.googleTranslate ?? false,
    cookie_banner: site.cookieBanner ?? null,
    cookie_banner_translations: site.cookieBannerTranslations ?? {},
    header_bg_color: site.headerBgColor ?? null,
    header_color: site.headerColor ?? null,
    header_color_bg: site.headerColorBg ?? null,
    header_color_txt: site.headerColorTxt ?? null,
    page_bg_color: site.pageBgColor ?? null,
    page_color: site.pageColor ?? null,
    page_color_bg: site.pageColorBg ?? null,
    page_color_txt: site.pageColorTxt ?? null,
    content_ids: site.contentIds ?? [],
    header_menu: site.headerMenu ?? [],
    footer: site.footer ?? { footerLinks: [] },
    service_map_country_ids: site.serviceMap?.countryIds ?? [],
    service_map_region_ids: site.serviceMap?.regionIds ?? [],
    service_map_city_ids: site.serviceMap?.cityIds ?? [],
    articles_all_categories: site.articles?.allCategories ?? true,
    articles_category_ids: site.articles?.categoryIds ?? [],
    oldid: site.oldid ?? null,
    created_at_ms: site.createdAt,
    updated_at_ms: site.updatedAt,
  }
}

function rowToSite(r: any): Site {
  return {
    id: r.id,
    name: r.name ?? undefined,
    baseUrl: r.base_url ?? undefined,
    logo: r.logo ?? undefined,
    defaultLocale: r.default_locale ?? undefined,
    locales: r.locales ?? [],
    googleTag: r.google_tag ?? [],
    transifexProject: r.transifex_project ?? undefined,
    googleTranslate: r.google_translate ?? false,
    cookieBanner: r.cookie_banner ?? undefined,
    cookieBannerTranslations: r.cookie_banner_translations ?? undefined,
    headerBgColor: r.header_bg_color ?? undefined,
    headerColor: r.header_color ?? undefined,
    headerColorBg: r.header_color_bg ?? undefined,
    headerColorTxt: r.header_color_txt ?? undefined,
    pageBgColor: r.page_bg_color ?? undefined,
    pageColor: r.page_color ?? undefined,
    pageColorBg: r.page_color_bg ?? undefined,
    pageColorTxt: r.page_color_txt ?? undefined,
    contentIds: r.content_ids ?? [],
    headerMenu: r.header_menu ?? [],
    footer: r.footer ?? { footerLinks: [] },
    serviceMap: {
      countryIds: r.service_map_country_ids ?? [],
      regionIds: r.service_map_region_ids ?? [],
      cityIds: r.service_map_city_ids ?? [],
    },
    articles: {
      allCategories: r.articles_all_categories ?? true,
      categoryIds: r.articles_category_ids ?? [],
    },
    oldid: r.oldid ?? undefined,
    createdAt: Number(r.created_at_ms),
    updatedAt: Number(r.updated_at_ms),
  }
}

function contentToRow(pc: PageContent, teamId: string) {
  // Everything that is not an own column becomes part of `fields`. Written
  // this way (rather than an explicit list) so a new block field needs no
  // change here -- the flat model gains fields regularly.
  const fields: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(pc)) {
    if (!CONTENT_OWN_COLUMNS.has(k) && v !== undefined) fields[k] = v
  }
  return {
    id: pc.id,
    team_id: teamId,
    type: pc.type,
    title: pc.title ?? null,
    fields,
    translations: pc.translations ?? {},
    oldid: pc.oldid ?? null,
    created_at_ms: pc.createdAt,
    updated_at_ms: pc.updatedAt,
  }
}

function rowToContent(r: any): PageContent {
  return {
    ...(r.fields ?? {}),
    id: r.id,
    type: r.type,
    title: r.title ?? undefined,
    translations: r.translations ?? {},
    oldid: r.oldid ?? undefined,
    createdAt: Number(r.created_at_ms),
    updatedAt: Number(r.updated_at_ms),
  } as PageContent
}

export function makeSiteConfigAdapter(teamId: string): StoreAdapter<SiteConfigStore> {
  const snapKey = (table: string, dbId: string) => `${teamId}:${dbId}:${table}`

  return {
    async load(dbId) {
      const [sitesRes, contentRes] = await Promise.all([
        cmsDb.from(SITE_TABLE).select("*").eq("team_id", teamId),
        cmsDb.from(CONTENT_TABLE).select("*").eq("team_id", teamId),
      ])
      if (sitesRes.error) throw sitesRes.error
      if (contentRes.error) throw contentRes.error

      // No rows at all means this team has never used the DB backend. Return
      // null so the caller can seed rather than showing an empty CMS as if
      // everything had been deleted.
      if (!sitesRes.data?.length && !contentRes.data?.length) return null

      const store = emptySiteConfigStore()
      const siteRows: Record<string, any> = {}
      const contentRows: Record<string, any> = {}

      for (const r of sitesRes.data ?? []) {
        store.sites[r.id] = rowToSite(r)
        siteRows[r.id] = siteToRow(store.sites[r.id], teamId)
      }
      for (const r of contentRes.data ?? []) {
        store.pageContents[r.id] = rowToContent(r)
        contentRows[r.id] = contentToRow(store.pageContents[r.id], teamId)
      }

      // Seed the snapshots from what we just read, so the first save writes
      // only genuine edits instead of the entire store.
      putSnapshot(snapKey(SITE_TABLE, dbId), siteRows)
      putSnapshot(snapKey(CONTENT_TABLE, dbId), contentRows)
      return store
    },

    async save(dbId, store) {
      const siteRows: Record<string, any> = {}
      for (const s of Object.values(store.sites)) siteRows[s.id] = siteToRow(s, teamId)
      const contentRows: Record<string, any> = {}
      for (const c of Object.values(store.pageContents)) contentRows[c.id] = contentToRow(c, teamId)

      const siteDiff = diffRows(getSnapshot(snapKey(SITE_TABLE, dbId)), siteRows)
      const contentDiff = diffRows(getSnapshot(snapKey(CONTENT_TABLE, dbId)), contentRows)

      /*
       * Order matters. Content is written BEFORE sites so a site referencing a
       * new block through content_ids never points at a row that does not
       * exist yet; deletes run last, after nothing references them.
       */
      const upsertContent = [...contentDiff.inserted, ...contentDiff.updated]
      if (upsertContent.length) {
        const { error } = await cmsDb.from(CONTENT_TABLE).upsert(upsertContent)
        if (error) throw error
      }

      const upsertSites = [...siteDiff.inserted, ...siteDiff.updated]
      if (upsertSites.length) {
        const { error } = await cmsDb.from(SITE_TABLE).upsert(upsertSites)
        if (error) throw error
      }

      if (siteDiff.deletedIds.length) {
        const { error } = await cmsDb.from(SITE_TABLE)
          .delete().in("id", siteDiff.deletedIds)
        if (error) throw error
      }
      if (contentDiff.deletedIds.length) {
        const { error } = await cmsDb.from(CONTENT_TABLE)
          .delete().in("id", contentDiff.deletedIds)
        if (error) throw error
      }

      // Only after every write succeeded -- updating the snapshot earlier
      // would make a failed write look applied, and the next save would skip it.
      if (!isEmptyDiff(siteDiff)) putSnapshot(snapKey(SITE_TABLE, dbId), siteRows)
      if (!isEmptyDiff(contentDiff)) putSnapshot(snapKey(CONTENT_TABLE, dbId), contentRows)
    },

    async clear(dbId) {
      const { error: e1 } = await cmsDb.from(CONTENT_TABLE)
        .delete().eq("team_id", teamId)
      if (e1) throw e1
      const { error: e2 } = await cmsDb.from(SITE_TABLE)
        .delete().eq("team_id", teamId)
      if (e2) throw e2
      putSnapshot(snapKey(SITE_TABLE, dbId), {})
      putSnapshot(snapKey(CONTENT_TABLE, dbId), {})
    },
  }
}
